import * as STD from '@emersya/mpa-std-lib';
import CM       from '@emersya/mpa-components-manager-lib';

export interface IInputComponentConfiguration<
	TValue,
	TValueSerialized
> extends CM.DOMComponent.IConfiguration {
	defaultValue? : TValue | TValueSerialized | null;
	isEditable? : boolean;
	/**
	 * MPAEngine do generic auto serialization of inputs components to make generic replay key in hand but some components should not be handled by the system, so you can use this parameters.
	 */
	disableMPAEngineAutoSerialization? : boolean;
	disableMPAEngineAutoDeserialization? : boolean;
};
const defaultValueMarker = Symbol('defaultValue');

export abstract class InputComponent<
	TNode extends HTMLElement | null,
	TValue,
	TValueSerialized,
	TComponent extends InputComponent<TNode, TValue, TValueSerialized, TComponent, TConfiguration>,
	TConfiguration extends IInputComponentConfiguration<TValue, TValueSerialized>
> extends CM.DOMComponent<
	TNode,
	TComponent,
	TConfiguration
>
{
	static getInputEventSubType() {
		return this.type;
	};

	static override initialize() {
		CM.Component.initialize.call(this);
		this.declareEventType('value', this.getInputEventSubType());
	};

	override async component_initialize() {
		await super.component_initialize();
		this.defaultValue = await this.deserialize(
			this.component_configuration.defaultValue ?? null
		);
	};

	static isDefaultValueMarker(marker : Symbol) {
		return (marker === defaultValueMarker);
	};

	value : TValue | null = null;
	defaultValue : TValue | null = null;
	isDefaultValueApplied = false;

	override component_hasValue() {
		return (true);
	};

	async applyDefault() {
		if (this.isDefaultValueApplied) {
			this.component_throwUserError('Default already applied!');
		}
		if (this.defaultValue === null) {
			this.component_throwLibraryError('');
		}
		await this.updateValue(this.defaultValue, defaultValueMarker);
		this.isDefaultValueApplied = true;
	};

	override async component_onActivationChange(event : STD.events.Event<any, CM.Component<any, any>>) {
		await super.component_onActivationChange(event);
		if (event.subType === 'enabled') {
			if (
				!this.isDefaultValueApplied &&
				(this.component_configuration.defaultValue !== undefined)
			)
			{
				await this.applyDefault();
			}
		} else {
		}
	};

	async updateValue(
		newValue : TValue | null,
		_defaultValueMarker? : Symbol
	)
	{
		const oldValue = this.value;
		this.value = newValue;
		const isDefaultValue = _defaultValueMarker === defaultValueMarker;
		await this.dispatchEvent('value', {
			subType : (<typeof InputComponent> <any> this.static).getInputEventSubType(),
			details : {
				oldValue,
				newValue,
				isDefaultValue
			}
		});
	};

	abstract deserialize(
		value : TValue | TValueSerialized | null
	) : Promise<TValue | null> | TValue | null;

	abstract serialize() : (TValueSerialized | Promise<TValueSerialized | null>) | null;

	override async component_start(startEvent : STD.events.Event<any, CM.Component<any, any>>) {
		await super.component_start(startEvent);
		if (!this.component_configuration.isEditable) {
			if (this.component_DOM) {
				this.component_DOM.classList.add('notEditable');
				(<any> this.component_DOM).disabled = true;
			}
		}
		if (!this.isDefaultValueApplied) {
			if (this.defaultValue !== null) {
				// if (!this.isDisabled) {
				await this.applyDefault();
				// }
			} else {
				this.isDefaultValueApplied = true; //set to true when there are no default value
			}
		}
	};

	abstract reset() : void | Promise<void>;

	abstract isValueTheDefault() : boolean;

	/**
	 * Return the current value or null if there are no value or the value is the default
	 **/
	getUserValue() {
		return (
			((this.value !== null) && !this.isValueTheDefault()) ? this.value :
				null
		);
	};

	async getUserValueSerialized() {
		return (
			((this.value !== null) && !this.isValueTheDefault()) ? await this.serialize() :
				null
		);
	};

	constructor(
		descriptor : ConstructorParameters<typeof CM.Component>[0],
		configuration : TConfiguration
	)
	{
		super(descriptor, configuration);
		if (this.component_configuration.isEditable === undefined) {
			this.component_configuration.isEditable = true;
		}
	};
};
export namespace NS {
	export const Component = InputComponent;
	export type Component<
		V extends HTMLElement | null,
		W,
		X,
		Y extends InputComponent<V, W, X, Y, Z>,
		Z extends IInputComponentConfiguration<W, X>
	> = InputComponent<V, W, X, Y, Z>;
	export type Configuration<X, Y> = IInputComponentConfiguration<X, Y>;
}
;
CM.declareAbstract({
	id :   'input',
	type : <any> InputComponent
});
